// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import Translation from '@app/features/messaging/state/Translation';
import ReverseImageSearch from '@app/features/search/state/ReverseImageSearch';
import SearchEngine from '@app/features/search/state/SearchEngine';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';
export type SearchProviderPickerMode = 'text' | 'image' | 'translate';

interface SearchProviderPickerModalProps {
	mode: SearchProviderPickerMode;
	onPick: (engineId: string) => void;
}
export const SearchProviderPickerModal: React.FC<SearchProviderPickerModalProps> = observer(({mode, onPick}) => {
	const {t} = useLingui();
	const isImageMode = mode === 'image';
	const isTranslateMode = mode === 'translate';
	const store = isImageMode ? ReverseImageSearch : isTranslateMode ? Translation : SearchEngine;
	const engines = store.engines;
	const options = useMemo<ReadonlyArray<SelectOption>>(
		() => engines.map((engine) => ({value: engine.id, label: engine.name})),
		[engines],
	);
	const [selectedEngineId, setSelectedEngineId] = useState<string>(
		() => store.defaultEngine?.id ?? store.effectiveDefaultEngine?.id ?? engines[0]?.id ?? '',
	);
	const [submitting, setSubmitting] = useState(false);
	const handleClose = useCallback(() => {
		ModalCommands.pop();
	}, []);
	const handleConfirm = useCallback(async () => {
		const engineId = engines.find((engine) => engine.id === selectedEngineId)?.id;
		if (!engineId) return;
		setSubmitting(true);
		try {
			await store.setDefaultEngine(engineId);
		} finally {
			setSubmitting(false);
		}
		ModalCommands.pop();
		onPick(engineId);
	}, [engines, onPick, selectedEngineId, store]);
	const title = isImageMode
		? t`Pick a reverse-image search provider`
		: isTranslateMode
			? t`Pick a translation provider`
			: t`Pick a web-search provider`;
	const description = isImageMode ? (
		<Trans>
			Choose where Fluxer should send images for reverse search. We'll remember your pick — you can change it or add
			your own provider later in Messages & Media settings under Search Engines.
		</Trans>
	) : isTranslateMode ? (
		<Trans>
			Choose where Fluxer should send highlighted text for translation. We'll remember your pick — you can change it or
			add your own provider later in Messages & Media settings under Translation.
		</Trans>
	) : (
		<Trans>
			Choose where Fluxer should search highlighted text. We'll remember your pick — you can change it or add your own
			provider later in Messages & Media settings under Search Engines.
		</Trans>
	);
	const selectLabel = isImageMode
		? t`Reverse-image search provider`
		: isTranslateMode
			? t`Translation provider`
			: t`Web-search provider`;
	const confirmLabel = isImageMode ? t`Search` : isTranslateMode ? t`Translate` : t`Search the Web`;
	const hasSelectedEngine = engines.some((engine) => engine.id === selectedEngineId);
	return (
		<Modal.Root size="small" onClose={handleClose}>
			<Modal.Header title={title} onClose={handleClose} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>{description}</Modal.Description>
					<Select label={selectLabel} value={selectedEngineId} options={options} onChange={setSelectedEngineId} />
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={handleClose}>
					{t`Cancel`}
				</Button>
				<Button
					variant="primary"
					onClick={handleConfirm}
					disabled={!hasSelectedEngine || submitting}
					submitting={submitting}
				>
					{confirmLabel}
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
